<script lang="ts">
    // Shared Utils/Jet
    import { loggerFor } from '@amp/web-apps-logger';
    import { getJetPerform } from '~/jet';

    // Config
    import { getShelfConfig } from '~/config/components/shelf';

    // Types
    import type { SvelteComponent } from 'svelte';
    import type { Section } from '@jet-app/music-classical/models/section/Section';

    // Components
    import HorizontalShelf from '~/components/jet/sections/HorizontalShelf.svelte';
    import InfoSection from '~/components/jet/sections/InfoSection.svelte';
    import ShelfList from '~/components/jet/sections/ShelfList.svelte';
    import Header from '@amp/web-app-components/src/components/Header/Header.svelte';

    export let shelf: Section<unknown>;
    export let itemComponent: typeof SvelteComponent;

    const log = loggerFor('<Shelf>');
    const perform = getJetPerform();

    $: itemKind = shelf.itemKind;
    $: items = shelf.items;
    $: hasTitleLink = !!shelf.segue;

    $: config = getShelfConfig(itemKind);
    $: arrowOffset = config?.arrowOffset;
    $: gridType = config?.gridType;
    $: gridRows = config?.gridRows;

    $: handleTitleClick = () => perform(shelf?.segue);

    /**
     * Example Header usage:
     *
     * ```svelte
     *     <Header
     *         title="Shelf Component Title"
     *         headline="Headline example"
     *         subtitle="Subtitle example"
     *         hasTitleLink={true}
     *       >
     *         <div slot="buttons">
     *             <a href="#"> button example </a>
     *         </div>
     *      </Header>
     * ```
     */
</script>

{#if shelf.title}
    <div
        id={shelf.id}
        data-testid="shelf-title-container"
        class="shelf-title-container"
    >
        <Header
            title={shelf.title}
            {hasTitleLink}
            on:titleClick={handleTitleClick}
        />
    </div>
{/if}

{#if itemKind === 'shelfListItem'}
    <ShelfList {shelf} {itemComponent} />
{:else if itemComponent && gridType}
    <!-- Default Shelf-->
    <HorizontalShelf {items} {gridType} {gridRows} {arrowOffset} let:item>
        <svelte:component this={itemComponent} {item} />
    </HorizontalShelf>
{:else if shelf.sectionKind === 'info'}
    <InfoSection section={shelf} />
{:else}
    {log.warn('unsupported shelf contentType:', itemKind, shelf)}
{/if}

<style lang="scss">
    .shelf-title-container {
        :global(.header) {
            align-items: center;
        }

        :global(.title) {
            margin-top: 3px;
            font: var(--title-2-emphasized-serif);
        }

        :global(.headline + .title) {
            margin-top: 0;
        }

        :global(.headline) {
            margin-top: 5px;
        }

        :global(.subtitle) {
            margin-top: 2px;
        }

        :global(.chevron) {
            --header-title-chevron-size: 13px;
        }
    }
</style>
